import { apiClient } from './apiClient';

/**
 * Document service for API operations (replaces lettersService)
 * Handles all document-related operations including upload, download, retrieval, and actions
 */
export class DocumentService {
    constructor() {
        this.BASE_URL = '/documents';
    }

    /**
     * Uploads a document using a FormData instance. This also saves it.
     * @param {FormData} formData - Must include file and metadata fields
     */
    async uploadDocument(formData) {
        if (!(formData instanceof FormData)) {
            throw new Error("uploadDocument expects a FormData instance.");
        }

        try {
            return await apiClient.upload(`${this.BASE_URL}`, formData);
        } catch (error) {
            alert("Form was generated successfully but there was an error uploading document. Please try again.");
            console.error("Error uploading document:", error);
            throw error;
        }
    }

    /**
     * Download a file from storage by URL path
     * @param {string} path - Full path to the file in storage
     */
    async downloadFileFromURL(path) {
        try {
            return await apiClient.getBlobData(`${this.BASE_URL}/download?fullPath=${encodeURIComponent(path)}`, {
                responseType: "blob"
            });
        } catch (error) {
            console.error("Error downloading file:", error);
            throw error;
        }
    }


    /**
     * Record an action performed on a document
     * @param {Object} params - Action parameters
     * @param {string} params.documentId - Document ID
     * @param {string} params.claimId - Claim ID (optional)
     * @param {string} params.action - Action type (download, print, view, share, delete)
     */
    async recordDocumentAction({ documentId, claimId, action }) {
        try {
            return await apiClient.post(`${this.BASE_URL}/action/record`, {
                documentId,
                claimId,
                action
            });
        } catch (error) {
            console.error("Error recording document action:", error);
            throw error;
        }
    }
    /**
     * Delete a document by file path
     * @param {string} documentId 
     */
    async deleteFile(documentId) {
        try {
            return await apiClient.delete(`${this.BASE_URL}/id/${documentId}`);
        } catch (error) {
            console.error("Error deleting file:", error);
            throw error;
        }
    }

    /**
     * Download a document by document ID
     * @param {string} documentId - Document ID
     */
    async downloadDocument(documentId) {
        try {
            return await apiClient.getBlobData(`${this.BASE_URL}/download/${documentId}`, {
                responseType: "blob"
            });
        } catch (error) {
            console.error("Error downloading document:", error);
            throw error;
        }
    }

    /**
     * Get a document by ID
     * @param {string} documentId - Document ID
     */
    async getDocument(documentId) {
        try {
            return await apiClient.get(`${this.BASE_URL}/id/${documentId}`);
        } catch (error) {
            console.error("Error fetching document:", error);
            throw error;
        }
    }

    /**
     * Search documents with unified API
     * @param {Object} params - Search parameters
     * @param {string} params.orgId - Organization ID
     * @param {string} params.queryText - Vector search query
     * @param {number} params.page - Page number
     * @param {number} params.limit - Items per page
     * @param {string} params.sortBy - Sort field
     * @param {string} params.sortOrder - Sort order (asc/desc)
     * @param {string} params.type - Document type filter
     * @param {string} params.subType - Document subtype filter
     * @param {string} params.claimId - Claim ID filter
     * @param {boolean} params.isSent - Sent status filter
     */
    async searchDocuments({
        orgId,
        page = 1,
        limit = 20,
        sortBy = 'createdAt',
        sortOrder = 'desc',
        documentType = '',
        category = '',
        categories = [],
        claimId = '',
        claimIds = [],
        patientId = '',
        patientIds = [],
        providerId = '',
        providerIds = [],
        rxNumber = '',
        serviceDateFrom = '',
        serviceDateTo = '',
        batchId = '',
        documentIds = [],
        isCurrent = undefined,
        includeEphemeral = undefined
    } = {}) {
        try {
            // Build query params
            const queryParams = new URLSearchParams();

            // Pagination
            if (page) queryParams.append('page', page.toString());
            if (limit) queryParams.append('limit', limit.toString());

            // Sorting
            if (sortBy) queryParams.append('sortBy', sortBy);
            if (sortOrder) queryParams.append('sortOrder', sortOrder);

            // Traditional filters
            // NOTE: If patientId is provided, do NOT include documentType in the query params
            if (documentType) queryParams.append('documentType', documentType);
            if (category) queryParams.append('category', category);
            if (categories && categories.length > 0) {
                categories.forEach((value) => queryParams.append('categories', value));
            }
            if (claimId) queryParams.append('claimId', claimId);
            if (claimIds && claimIds.length > 0) {
                claimIds.forEach((value) => queryParams.append('claimIds', value));
            }
            if (patientId) queryParams.append('patientIds', patientId);
            if (patientIds && patientIds.length > 0) {
                patientIds.forEach((value) => queryParams.append('patientIds', value));
            }
            if (providerId) queryParams.append('providerId', providerId);
            if (providerIds && providerIds.length > 0) {
                providerIds.forEach((value) => queryParams.append('providerIds', value));
            }
            if (rxNumber) queryParams.append('rxNumber', rxNumber);
            if (batchId) queryParams.append('batchId', batchId);
            if (documentIds && documentIds.length > 0) {
                documentIds.forEach(id => queryParams.append('documentIds', id));
            }
            if (serviceDateFrom) queryParams.append('serviceDateFrom', serviceDateFrom);
            if (serviceDateTo) queryParams.append('serviceDateTo', serviceDateTo);
            if (isCurrent !== undefined) queryParams.append('isCurrent', isCurrent);
            if (includeEphemeral !== undefined) queryParams.append('includeEphemeral', includeEphemeral.toString());

            const url = `${this.BASE_URL}/search?${queryParams.toString()}`;
            const response = await apiClient.post(url, { orgId });

            return response.data || response;
        } catch (error) {
            console.error('Error searching documents:', error);
            throw error;
        }
    }


    /**
     * List files by type
     * @param {string} type - Document type
     * @param {string} subtype - Document subtype (optional)
     */
    async listFilesByType(type, subtype) {
        try {
            let url = `${this.BASE_URL}/list/byType?type=${encodeURIComponent(type)}`;
            if (subtype) url += `&subtype=${encodeURIComponent(subtype)}`;
            return await apiClient.get(url);
        } catch (error) {
            console.error("Error listing files by type:", error);
            throw error;
        }
    }

    /**
     * Update a document with new metadata/status
     * @param {string} documentId - The document ID
     * @param {object} updateData - Fields to update
     */
    async updateDocument(documentId, updateData) {
        try {
            if (!documentId) {
                throw new Error('Document ID is required');
            }

            const response = await apiClient.put(
                `/documents/id/${documentId}`,
                updateData
            );

            if (response?.data) {
                return response.data;
            }

            return response;
        } catch (error) {
            console.error('Error updating document:', error);
            throw error;
        }
    }

    /**
     * Generate a Lien (Letter of Protection) PDF for a claim and save it to the Document Center.
     * No patient signature required — for print/mail use.
     * @param {string} claimId - The claim ID
     * @param {string} [language='english'] - Language ('english' | 'spanish')
     */
    async generateLien(claimId, language = 'english') {
        try {
            if (!claimId) throw new Error('claimId is required');
            return await apiClient.post(`${this.BASE_URL}/generate-lien`, { claimId, language });
        } catch (error) {
            console.error('Error generating lien document:', error);
            throw error;
        }
    }

    /**
     * Generate a blank Lien (Letter of Protection) PDF template with empty fields.
     * Can be used without a claim/patient - for manual filling.
     * @param {string} [language='english'] - Language ('english' | 'spanish')
     */
    async generateBlankLien(language = 'english') {
        try {
            return await apiClient.post(`${this.BASE_URL}/generate-blank-lien`, { language });
        } catch (error) {
            console.error('Error generating blank lien document:', error);
            throw error;
        }
    }

    /**
     * Send lien template to patient's mobile number via SMS
     * @param {string} claimId - The claim ID
     * @param {string} patientPhone - Patient's mobile phone number
     */
    async sendLienSms(claimId, patientPhone) {
        try {
            if (!claimId) throw new Error('claimId is required');
            if (!patientPhone) throw new Error('patientPhone is required');
            return await apiClient.post(`${this.BASE_URL}/send-lien-sms`, { claimId, patientPhone });
        } catch (error) {
            console.error('Error sending lien SMS:', error);
            throw error;
        }
    }

    /**
     * Executes the splicing of EOB pages for multiple patient assignments.
     * @param {string} documentId - Parent document ID
     * @param {Array} assignments - Splicing assignments: [{ patientId, patientName, claimId, pageNumbers, metadata }]
     */
    async executeSplice(documentId, assignments) {
        try {
            if (!documentId) throw new Error('documentId is required');
            if (!assignments || assignments.length === 0) throw new Error('assignments are required');
            return await apiClient.post('/llm/eob/execute-splice', { documentId, assignments });
        } catch (error) {
            console.error('Error executing EOB splice:', error);
            throw error;
        }
    }

}

export const documentService = new DocumentService();

